import Component from "../../lib/Component.js";
import ScrollInView from "./ScrollInView.js";

/**
 *
 * @param {{yearMonthsLinks: HTMLAnchorElement[], sectionSelector?: string, archiveCollapse?: {show: () => void, hide: () => void, isHidden: () => boolean}}} param0
 * @returns
 */
const MonthNavigator = ({
  yearMonthsLinks,
  sectionSelector = "section.item-list-section.year-movies",
  archiveCollapse = null,
}) => {
  /** @type {{[firstWeekOfMonth: string]: HTMLAnchorElement}} */
  const yearMonthsAnchors = {};
  yearMonthsLinks.forEach((link) => {
    const firstWeekOfMonth = link.getAttribute("href").split("#")[1];
    yearMonthsAnchors[firstWeekOfMonth] = link;
  });

  /** @type {string | null} */
  let currentMonth = null;
  /** @type {{[monthName: string]: { monthLink: HTMLAnchorElement, sections: {[weekName: string]: HTMLElement}}}} */
  const yearMonthsSections = {};
  document.querySelectorAll(".item-list-section-anchor").forEach((sectionAnchor) => {
    const weekName = sectionAnchor.getAttribute("id");
    if (weekName && yearMonthsAnchors[weekName]) {
      currentMonth = yearMonthsAnchors[weekName].textContent.trim();
    }
    if (currentMonth) {
      if (!yearMonthsSections[currentMonth]) {
        yearMonthsSections[currentMonth] = {
          monthLink: yearMonthsAnchors[weekName],
          sections: {},
        };
      }
      yearMonthsSections[currentMonth].sections[weekName] = sectionAnchor.closest("section");

      if (
        !yearMonthsSections[currentMonth].sections[weekName].querySelector("h2.empty-list") &&
        !yearMonthsSections[currentMonth].sections[weekName].classList.contains("skip")
      ) {
        yearMonthsSections[currentMonth].monthLink.parentElement.classList.remove("disabled");
      }
    }
  });

  /**
   * @param {string} weekName
   * @return {HTMLAnchorElement | null}
   */
  const findMonthLinkByWeekName = (weekName) => {
    for (const [, { monthLink, sections }] of Object.entries(yearMonthsSections)) {
      if (sections[weekName]) {
        return monthLink;
      }
    }
    return null;
  };

  /**
   * @param {HTMLElement} section
   * @return {HTMLAnchorElement | null}
   */
  const findMonthLinkBySection = (section) => {
    const weekName = section.querySelector(".item-list-section-anchor")?.getAttribute("id");
    if (weekName) {
      return findMonthLinkByWeekName(weekName);
    }
    return null;
  };

  const updateAnchorPositions = () => {
    document.querySelectorAll(".item-list-section-anchor").forEach((sectionAnchor) => {
      const header = document.querySelector("header#header");
      const headerHeight = header.offsetHeight - header.offsetTop;

      sectionAnchor.style.marginTop = `-${headerHeight}px`;
    });
  };

  let scollingTimeout = null;
  let scrollStarted = null;
  let scrollEndTimeout = null;
  const handleScoll = () => {
    if (!scrollStarted) {
      scrollStarted = Date.now();
    }
    if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
    scrollEndTimeout = window.setTimeout(() => {
      scrollStarted = null;
    }, 200);
    if (Date.now() - scrollStarted > 500) {
      yearMonthsLinks.forEach((link) => link.parentElement.classList.add("scrolling"));
    }
    if (scollingTimeout) clearTimeout(scollingTimeout);
    scollingTimeout = window.setTimeout(() => {
      yearMonthsLinks.forEach((link) => link.parentElement.classList.remove("scrolling"));
    }, 1000);

    updateAnchorPositions();
  };

  const scrollInView = ScrollInView({
    componentName: "MonthNavigator.ScrollInView",
    observables: document.querySelectorAll(sectionSelector),
    onEnter: (section) => {
      const monthLink = findMonthLinkBySection(section);
      if (monthLink) {
        yearMonthsLinks.forEach((link) => link.parentElement.classList.remove("active"));
        monthLink.parentElement.classList.add("active");
      }
    },
    options: {
      threshold: [0, 0.1, 0.5, 1.0],
      rootMargin: "-30% 0px -70% 0px",
    },
  });

  const handleYearMonthLinkClick = (event) => {
    event.preventDefault();
    const sectionId = event.target.closest("a").href.split("#")[1];
    const targetSection = document.getElementById(sectionId);

    // If target section is a past week and archive is hidden, expand it first
    if (archiveCollapse && archiveCollapse.isHidden()) {
      archiveCollapse.show();
      // Give a tiny delay for the DOM to update before scrolling
      setTimeout(() => {
        const scrollAdjustment = window.innerWidth >= 1200 ? 32 : 0;
        window.scrollTo({
          top: targetSection.offsetTop - scrollAdjustment,
          behavior: "smooth",
        });
      }, 50);
    } else {
      const scrollAdjustment = window.innerWidth >= 1200 ? 32 : 0;
      window.scrollTo({
        top: targetSection.offsetTop - scrollAdjustment,
        behavior: "smooth",
      });
    }
  };

  return new Component({
    onMount: () => {
      scrollInView.mount();
      document.addEventListener("scroll", handleScoll);
      yearMonthsLinks.forEach((link) => link.addEventListener("click", handleYearMonthLinkClick));
    },
    onUnmount: () => {
      scrollInView.unmount();
      document.removeEventListener("scroll", handleScoll);
      yearMonthsLinks.forEach((link) => link.removeEventListener("click", handleYearMonthLinkClick));
    },
    render: () => {
      updateAnchorPositions();
    },
  });
};

export default MonthNavigator;
